import Vue from 'vue';
import Component from 'vue-class-component';
import {Emit, Mixins, Prop} from 'vue-property-decorator';
import {BoardUsersMixin} from '@/mixins/common/board-users.mixin';
import BrowserService from '@/providers/browser-utils/browser.service';
import EdPresentationModeFactory from '@/content/factories/ed-presentation-mode.factory';
import {HOMEWORK_SOCKET_EVENTS, HOMEWORK_SOCKET_MESSAGES_TYPES} from '@/homework/values/homeworks.values';
import {Section} from '@/courses/models/section.model';
import WebSocketMessageResponse from '@/content/models/web-socket-message-response.model';
import KEYS from '@/values/keys';
import {HOMEWORKS_STORE} from '@/homework/store/homeworks.store';
import {BOARD_EVENTS} from '@/values/ed-whiteboard.values';
import {EMIT_JOIN_SOCKET_EVENT} from '@/core/values/app.values';

@Component({})
class EdWhiteboardUsers extends Mixins(BoardUsersMixin) {
    @Prop({required: false}) readonly section: Section;
    @Prop({required: false}) readonly history: any;
    @Prop({required: false}) readonly currentBoardPage : any;

    get isCurrentUserStudent(): boolean {
        return this.$store.state.AuthStore.role === KEYS.ROLES.BUYER;
    }

    get homeworkOwnerId(): number {
        return this.section.studentId;
    }

    get isHistory(){
        return this.history && this.history.show ? true : false;
    }

    get shouldNotReportPresence(): boolean {
        return (this.homeworkOwnerId && this.isCurrentUserStudent && this.homeworkOwnerId != this.currentUser.id) || this.isHistory;
    }

    @Emit()
    public goToPage(page: number) : number {
        return page;
    }

    private onUserJoinedTask(message: WebSocketMessageResponse): void {
        this.$store.commit(HOMEWORKS_STORE.MUTATIONS.UPDATE_ONLINE_HOMEWORK_DATA, message);
    }

    private onUserLeftTask(message: WebSocketMessageResponse): void {
        this.$store.commit(HOMEWORKS_STORE.MUTATIONS.UPDATE_ONLINE_HOMEWORK_DATA, message);
    }

    private onUserChangedPage(message: WebSocketMessageResponse): void {
        this.$store.commit(HOMEWORKS_STORE.MUTATIONS.UPDATE_ONLINE_HOMEWORK_PAGE, message);
    }

    private emitJoin(): void {
        if (this.section) {
            if (this.shouldNotReportPresence) {
                return; // Do not emit presence event for students joining another student's homework.
            }
            const topicId = this.section.classId;
            const content = {
                sectionId: this.section.sectionId,
                homeworkId: this.homeworkId,
                page: this.currentBoardPage ? this.currentBoardPage : 1,
                fname: this.currentUser.firstName,
                lname: this.currentUser.lastName,
                device: BrowserService.getFullBrowserInfo()
            };
            const msgType = this.isCurrentUserStudent
                ? HOMEWORK_SOCKET_MESSAGES_TYPES.studentConnect
                : HOMEWORK_SOCKET_MESSAGES_TYPES.teacherConnect;

            this.$EdSocket.emit(HOMEWORK_SOCKET_EVENTS.join,
                EdPresentationModeFactory.createWebSocketMessageRequest(msgType, String(topicId), false,
                    JSON.stringify(content)));
            this.listenToTaskSocketEvents();
        }
    }


    private emitLeave(): void {
        if (this.section) {
            if (this.shouldNotReportPresence) {
                return;
            }
            const topicId = this.section.classId;
            const content = {
                sectionId: this.section.sectionId,
                homeworkId: this.homeworkId,
                device: BrowserService.getFullBrowserInfo()
            };

            this.$EdSocket.emit(HOMEWORK_SOCKET_EVENTS.leave,
                EdPresentationModeFactory.createWebSocketMessageRequest(HOMEWORK_SOCKET_MESSAGES_TYPES.leave,
                    String(topicId), false, JSON.stringify(content)));

            // We are no longer interested in following this task's topic
            this.$EdSocket.off(HOMEWORK_SOCKET_EVENTS.taskTopic.replace('{taskId}', String(this.homeworkId)));
        }
    }

    private listenToTaskSocketEvents(): void {
        this.$EdSocket.on(HOMEWORK_SOCKET_EVENTS.taskTopic.replace('{taskId}', String(this.homeworkId)),
            (message: WebSocketMessageResponse) => {
                if (message.messageType == HOMEWORK_SOCKET_MESSAGES_TYPES.changePage) {
                    this.onUserChangedPage(message);
                } else if (message.messageType == HOMEWORK_SOCKET_MESSAGES_TYPES.leave) {
                    this.onUserLeftTask(message);
                } else if (message.messageType == HOMEWORK_SOCKET_MESSAGES_TYPES.studentConnect
                    || message.messageType == HOMEWORK_SOCKET_MESSAGES_TYPES.teacherConnect
                    || message.messageType == HOMEWORK_SOCKET_MESSAGES_TYPES.teacherConnect) {
                    this.onUserJoinedTask(message);
                }
            });
    }


    mounted() {
        this.emitJoin();
        this.$edEventBus.$on(BOARD_EVENTS.RECONNECT_BOARD, this.emitJoin);
        this.$edEventBus.$on(EMIT_JOIN_SOCKET_EVENT, this.emitJoin);
    }

    beforeDestroy() {
        this.emitLeave();
        this.$edEventBus.$off(BOARD_EVENTS.RECONNECT_BOARD);
        this.$edEventBus.$off(EMIT_JOIN_SOCKET_EVENT);
    }
}

export default Vue.component('ed-whiteboard-users', EdWhiteboardUsers);